import { useMemo } from "react";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";

interface StatsDisplayBaseProps {
  title: string;
  value: number | string;
  caption?: string;
}

const StatsDisplayBase = ({ title, value, caption }: StatsDisplayBaseProps) => (
  <Stack
    className="w-[14.375rem] py-[14px]"
    gap="none"
    justify="center"
    align="center"
  >
    <Typography variant="statNumber">{value}</Typography>
    <Stack direction="row" align="center" gap="xs" className="justify-center">
      <Typography
        variant="subtitleW2"
        textColor="tertiary"
        className="text-center"
      >
        {title}
      </Typography>
    </Stack>
    {caption && (
      <Typography
        variant="captionW1"
        textColor="tertiary"
        className="mt-1 text-center"
      >
        {caption}
      </Typography>
    )}
  </Stack>
);

interface StatsDisplayProps extends StatsDisplayBaseProps {
  isLoading?: boolean;
  tooltipDescription?: React.ReactNode;
}

export const StatsDisplay = ({
  title,
  value,
  caption,
  isLoading,
  tooltipDescription,
}: StatsDisplayProps) => {
  if (isLoading) {
    return (
      <Stack
        className="w-[14.375rem] py-[14px]"
        justify="center"
        align="center"
        gap="sm"
      >
        <Skeleton className="h-[32px] w-[50px]" />
        <Skeleton className="h-[16px] w-[100px]" />
        <Skeleton className="h-[10px] w-[80px]" />
      </Stack>
    );
  }

  const base = useMemo(
    () => <StatsDisplayBase title={title} value={value} caption={caption} />,
    [title, value, caption],
  );

  if (tooltipDescription) {
    return (
      <Tooltip>
        <TooltipTrigger>{base}</TooltipTrigger>
        <TooltipContent>{tooltipDescription}</TooltipContent>
      </Tooltip>
    );
  }

  return base;
};
